// Pill "Log outcome" → the Call Logs outcome form (OutcomeDialog).
//
// That form records against a missed inbound call that still owes a call back
// (contract B4 §3: any other call is a 400). The pill's ended call is the
// conversation itself, so look up the newest open missed call from the same
// number and log against that. No such call = nothing is owed, and the caller
// falls back to Call Logs filtered to the number.
import { callApi } from '../../utils/apiCall';
import { normalizeUnifiedCall } from '../../pages/dashboard/callLogs/callLog.normalize';
import { ORDER_NEWEST_FIRST, listUrl } from '../../pages/dashboard/callLogs/callLogQuery';

const LOOKUP_SIZE = 5;
// The pill has already closed, so a slow answer must not navigate much later.
const LOOKUP_OPTIONS = { bustCache: true, suppressServerErrorDispatch: true, timeout: 5000, maxRetries: 0 };
// Enough digits to tell two numbers apart however each is written
// (+44 7911 123456 vs 07911123456).
const MATCH_DIGITS = 9;

const tail = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.length >= MATCH_DIGITS ? digits.slice(-MATCH_DIGITS) : '';
};

export const sameNumber = (a, b) => Boolean(tail(a)) && tail(a) === tail(b);

export const owedCallsUrl = (practiceId, number) => listUrl(practiceId, {
    page_mode: 'fast',
    page: 1,
    page_size: LOOKUP_SIZE,
    ordering: ORDER_NEWEST_FIRST,
    q: number,
    direction: 'inbound',
    outcome: 'missed',
    follow_up_state: 'open',
    include_test: 'false',
});

// The search box also matches names and partial numbers, so keep only rows
// whose caller really is this number.
export const pickOwedCall = (rows, number) => (Array.isArray(rows) ? rows : [])
    .find((row) => row && row.direction === 'inbound' && sameNumber(row.contact, number)) || null;

// { row } when an open missed call from `number` exists, else { row: null }.
// Never throws; any error, a 5 s timeout or a malformed row resolves to { row: null }.
export async function findOwedMissedCall(practiceId, number) {
    if (!practiceId || !tail(number)) return { row: null };
    const response = await callApi(owedCallsUrl(practiceId, number), 'GET', null, false, LOOKUP_OPTIONS);
    if (!response?.ok || !Array.isArray(response.data?.results)) return { row: null };
    try {
        return { row: pickOwedCall(response.data.results.map(normalizeUnifiedCall), number) };
    } catch {
        return { row: null };
    }
}
